import { lazy, Suspense, useEffect, useMemo, useState } from 'react';
import { BrowserRouter, Navigate, Route, Routes, useLocation, useSearchParams } from 'react-router-dom';
import { QueryClientProvider } from '@tanstack/react-query';
import { Toaster } from 'sonner';
import { AuthProvider, useAuth } from '@/auth/AuthProvider';
import { PERMISSIONS, type Permission } from '@/auth/permissions';
import { createQueryClient } from './queryClient';
import { AppErrorBoundary } from './ErrorBoundary';
import { ThemeProvider, useTheme } from './ThemeProvider';
import { AppShell } from './AppShell';
import { SignInPage } from './SignInPage';
import { DetailSkeleton } from '@/design-system/Feedback';
import { ForbiddenPage, NotFoundPage } from './StatusPages';

/**
 * Routes are lazily loaded so the initial bundle carries the shell and the dashboard, not every
 * screen in the product. Each chunk arrives when its route is first visited.
 */
const DashboardPage = lazy(() => import('@/features/dashboard/DashboardPage'));
const AssetsPage = lazy(() => import('@/features/assets/AssetsPage'));
const AssetDetailPage = lazy(() => import('@/features/assets/AssetDetailPage'));
const AssetScanResolver = lazy(() => import('@/features/assets/AssetScanResolver'));
const StocktakePage = lazy(() => import('@/features/stocktakes/StocktakePage'));
const AssignmentsPage = lazy(() => import('@/features/assignments/AssignmentsPage'));
const ConsumablesPage = lazy(() => import('@/features/consumables/ConsumablesPage'));
const SettingsPage = lazy(() => import('@/features/settings/SettingsPage'));
const ModuleRedirectPage = lazy(() => import('./modules'));
const DecommissionPage = lazy(() => import('@/features/decommission/DecommissionPage'));
const UserManualPage = lazy(() => import('@/features/manual/UserManualPage'));
const MyAssetsPage = lazy(() => import('@/features/acceptance/MyAssetsPage'));
const AcceptancePage = lazy(() => import('@/features/acceptance/AcceptancePage'));
const EmployeesPage = lazy(() => import('@/features/employees/EmployeesPage'));
const EmployeeDetailPage = lazy(() => import('@/features/employees/EmployeeDetailPage'));
const ProcurementPage = lazy(() => import('@/features/procurement/ProcurementPage'));
const ProcurementDetailPage = lazy(() => import('@/features/procurement/ProcurementDetailPage'));
const PreparationPage = lazy(() => import('@/features/preparation/PreparationPage'));
const PreparationRunPage = lazy(() => import('@/features/preparation/PreparationRunPage'));
const ReportsPage = lazy(() => import('@/features/reports/ReportsPage'));
const EndpointsPage = lazy(() => import('@/features/endpoints/EndpointsPage'));
const SoftwareLicencesPage = lazy(() => import('@/features/softwarelicences/SoftwareLicencesPage'));
const EndpointDetailPage = lazy(() => import('@/features/endpoints/EndpointDetailPage'));
const VelociraptorEndpointDetailPage = lazy(() => import('@/features/endpoints/VelociraptorEndpointDetailPage'));
const DiscoveryPage = lazy(() => import('@/features/discovery/DiscoveryPage'));
const ImportsPage = lazy(() => import('@/features/imports/ImportsPage'));
const ServiceDeskPage = lazy(() => import('@/features/servicedesk/ServiceDeskPage'));
const TicketDetailPage = lazy(() => import('@/features/servicedesk/TicketDetailPage'));
const AuditPage = lazy(() => import('@/features/audit/AuditPage'));
const NotificationsPage = lazy(() => import('@/features/notifications/NotificationsPage'));
const PlatformPage = lazy(() => import('@/features/platform/PlatformPage'));
const TenantDetailPage = lazy(() => import('@/features/platform/TenantDetailPage'));
const PlansPage = lazy(() => import('@/features/platform/PlansPage'));

/** Maintenance and warranty / AMC screens, each behind the permission its data needs. */
const MAINTENANCE_ROUTES = [
  {
    path: '/maintenance',
    anyOf: [PERMISSIONS.MAINTENANCE_READ],
    Page: lazy(() => import('@/features/maintenance/MaintenancePage')),
  },
  {
    path: '/maintenance/work-orders/:workOrderId',
    anyOf: [PERMISSIONS.MAINTENANCE_READ],
    Page: lazy(() => import('@/features/maintenance/WorkOrderDetailPage')),
  },
  {
    path: '/contracts',
    anyOf: [PERMISSIONS.CONTRACT_READ],
    Page: lazy(() => import('@/features/contracts/ContractsPage')),
  },
  {
    path: '/contracts/:contractId',
    anyOf: [PERMISSIONS.CONTRACT_READ],
    Page: lazy(() => import('@/features/contracts/ContractDetailPage')),
  },
];

export function App() {
  const queryClient = useMemo(() => createQueryClient(), []);

  return (
    <AppErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <ThemeProvider>
          <BrowserRouter>
            <AuthProvider>
              <AppToaster />
              <AppRoutes />
            </AuthProvider>
          </BrowserRouter>
        </ThemeProvider>
      </QueryClientProvider>
    </AppErrorBoundary>
  );
}

function AppToaster() {
  const { theme } = useTheme();
  return (
    <Toaster
      theme={theme}
      position="bottom-right"
      closeButton
      richColors
      // Long enough to read a sentence, short enough not to obstruct the interface. Anything the
      // user must remember is written into the page, never left to a toast.
      duration={5000}
    />
  );
}

/**
 * Where "/" goes. A platform operator holds no company permissions, so the company dashboard would
 * show them an empty page; their home is the platform console instead.
 */
function HomeRedirect() {
  const { can, canAny } = useAuth();
  const [searchParams] = useSearchParams();
  const location = useLocation();

  const returnToParam = searchParams.get('returnTo');
  const stateFrom = (location.state as { from?: { pathname: string; search?: string; hash?: string } } | null)?.from;
  const rawTarget =
    returnToParam ||
    (stateFrom ? `${stateFrom.pathname}${stateFrom.search ?? ''}${stateFrom.hash ?? ''}` : null);

  const isSafe = Boolean(
    rawTarget &&
      rawTarget.startsWith('/') &&
      !rawTarget.startsWith('//') &&
      !rawTarget.startsWith('/\\') &&
      !rawTarget.includes('://') &&
      !rawTarget.startsWith('/sign-in') &&
      rawTarget !== '/',
  );

  const target = isSafe ? rawTarget : null;

  const operatorOnly =
    can(PERMISSIONS.PLATFORM_READ) &&
    !canAny([PERMISSIONS.ASSET_READ, PERMISSIONS.ASSET_READ_OWN, PERMISSIONS.REPORT_READ]);
  return <Navigate to={target ?? (operatorOnly ? '/platform' : '/dashboard')} replace />;
}

/** The platform console for the operator; everyone else's home for anyone else. */
function PlatformOrHome({ children }: { children: React.ReactNode }) {
  const { can } = useAuth();
  return can(PERMISSIONS.PLATFORM_READ) ? <>{children}</> : <Navigate to="/dashboard" replace />;
}

/** Blocks a route until the user holds the permission, rather than rendering a screen of 403s. */
function RequirePermission({
  anyOf,
  children,
}: {
  anyOf: Permission[];
  children: React.ReactNode;
}) {
  const { canAny } = useAuth();
  return canAny(anyOf) ? <>{children}</> : <ForbiddenPage />;
}

function AppRoutes() {
  const { status } = useAuth();
  const location = useLocation();
  const [passwordLinkActive, setPasswordLinkActive] = useState(() =>
    window.location.pathname === '/sign-in' && window.location.hash.startsWith('#password-token='),
  );

  useEffect(() => {
    const trackPasswordLink = () => {
      if (
        window.location.pathname === '/sign-in' &&
        window.location.hash.startsWith('#password-token=')
      ) {
        setPasswordLinkActive(true);
      }
    };
    window.addEventListener('hashchange', trackPasswordLink);
    return () => window.removeEventListener('hashchange', trackPasswordLink);
  }, []);

  // Recovery links must remain usable when opened in a tab that still has a now-revoked session.
  if (location.pathname === '/sign-in' && passwordLinkActive) {
    return <SignInPage onPasswordReset={() => setPasswordLinkActive(false)} />;
  }

  // A stored token is still being revalidated: showing sign-in here would flash a login screen at
  // a user who is already signed in, and lose the page they refreshed on.
  if (status === 'unknown') {
    return <DetailSkeleton />;
  }

  if (status !== 'authenticated') {
    const currentPath = location.pathname + location.search + location.hash;
    const isSignIn = location.pathname === '/sign-in';
    const isRoot = location.pathname === '/' || !location.pathname;

    if (isSignIn) {
      return (
        <Routes>
          <Route path="/sign-in" element={<SignInPage />} />
          <Route
            path="*"
            element={
              <Navigate
                to={!isRoot && !isSignIn ? `/sign-in?returnTo=${encodeURIComponent(currentPath)}` : '/sign-in'}
                replace
              />
            }
          />
        </Routes>
      );
    }

    const redirectPath = !isRoot
      ? `/sign-in?returnTo=${encodeURIComponent(currentPath)}`
      : '/sign-in';

    return (
      <Navigate
        to={redirectPath}
        replace
        state={{ from: location }}
      />
    );
  }

  return (
    <Routes>
      <Route path="/sign-in" element={<HomeRedirect />} />
      <Route element={<AppShell />}>
        <Route index element={<HomeRedirect />} />

        <Route
          path="/dashboard"
          element={
            <LazyRoute>
              <DashboardPage />
            </LazyRoute>
          }
        />

        <Route
          path="/my-assets"
          element={
            <LazyRoute>
              <MyAssetsPage />
            </LazyRoute>
          }
        />
        <Route
          path="/my-assets/:assignmentId/decision"
          element={
            <LazyRoute>
              <AcceptancePage />
            </LazyRoute>
          }
        />

        <Route
          path="/assets"
          element={
            <RequirePermission anyOf={[PERMISSIONS.ASSET_READ]}>
              <LazyRoute>
                <AssetsPage />
              </LazyRoute>
            </RequirePermission>
          }
        />
        <Route path="/a/:key" element={<RequirePermission anyOf={[PERMISSIONS.ASSET_READ]}>
          <LazyRoute><AssetScanResolver /></LazyRoute>
        </RequirePermission>} />
        <Route path="/stocktakes" element={<RequirePermission anyOf={[PERMISSIONS.ASSET_READ]}>
          <LazyRoute><StocktakePage /></LazyRoute>
        </RequirePermission>} />
        <Route path="/stocktakes/:stocktakeId" element={<RequirePermission anyOf={[PERMISSIONS.ASSET_READ]}>
          <LazyRoute><StocktakePage /></LazyRoute>
        </RequirePermission>} />
        <Route
          path="/assets/:assetId"
          element={
            <RequirePermission anyOf={[PERMISSIONS.ASSET_READ]}>
              <LazyRoute>
                <AssetDetailPage />
              </LazyRoute>
            </RequirePermission>
          }
        />

        <Route path="/decommission" element={<RequirePermission anyOf={[PERMISSIONS.ASSET_READ]}>
          <LazyRoute><DecommissionPage key="queue" view="queue" /></LazyRoute>
        </RequirePermission>} />
        <Route path="/data-sanitisation" element={<RequirePermission anyOf={[PERMISSIONS.ASSET_READ]}>
          <LazyRoute><DecommissionPage key="sanitisation" view="sanitisation" /></LazyRoute>
        </RequirePermission>} />

        <Route
          path="/assignments"
          element={
            <RequirePermission anyOf={[PERMISSIONS.ASSET_READ, PERMISSIONS.ASSET_ASSIGN]}>
              <LazyRoute>
                <AssignmentsPage />
              </LazyRoute>
            </RequirePermission>
          }
        />

        <Route
          path="/consumables"
          element={
            <RequirePermission anyOf={[PERMISSIONS.CONSUMABLE_READ]}>
              <LazyRoute>
                <ConsumablesPage />
              </LazyRoute>
            </RequirePermission>
          }
        />

        <Route
          path="/employees"
          element={
            <RequirePermission anyOf={[PERMISSIONS.EMPLOYEE_READ]}>
              <LazyRoute>
                <EmployeesPage />
              </LazyRoute>
            </RequirePermission>
          }
        />
        <Route
          path="/employees/:employeeId"
          element={
            <RequirePermission anyOf={[PERMISSIONS.EMPLOYEE_READ]}>
              <LazyRoute>
                <EmployeeDetailPage />
              </LazyRoute>
            </RequirePermission>
          }
        />

        <Route
          path="/procurement"
          element={
            <RequirePermission
              anyOf={[PERMISSIONS.PROCUREMENT_READ, PERMISSIONS.PROCUREMENT_WRITE]}
            >
              <LazyRoute>
                <ProcurementPage />
              </LazyRoute>
            </RequirePermission>
          }
        />
        <Route
          path="/procurement/:orderId"
          element={
            <RequirePermission
              anyOf={[PERMISSIONS.PROCUREMENT_READ, PERMISSIONS.PROCUREMENT_WRITE]}
            >
              <LazyRoute>
                <ProcurementDetailPage />
              </LazyRoute>
            </RequirePermission>
          }
        />

        <Route
          path="/preparation"
          element={
            <RequirePermission anyOf={[PERMISSIONS.PREPARATION_READ, PERMISSIONS.PREPARATION_RUN]}>
              <LazyRoute>
                <PreparationPage />
              </LazyRoute>
            </RequirePermission>
          }
        />
        <Route
          path="/preparation/:runId"
          element={
            <RequirePermission anyOf={[PERMISSIONS.PREPARATION_READ, PERMISSIONS.PREPARATION_RUN]}>
              <LazyRoute>
                <PreparationRunPage />
              </LazyRoute>
            </RequirePermission>
          }
        />

        <Route
          path="/audit"
          element={
            <RequirePermission anyOf={[PERMISSIONS.AUDIT_READ]}>
              <LazyRoute>
                <AuditPage />
              </LazyRoute>
            </RequirePermission>
          }
        />

        <Route
          path="/reports"
          element={
            <RequirePermission anyOf={[PERMISSIONS.REPORT_READ]}>
              <LazyRoute>
                <ReportsPage />
              </LazyRoute>
            </RequirePermission>
          }
        />
        <Route
          path="/endpoints/velociraptor/:integrationId"
          element={
            <RequirePermission anyOf={[PERMISSIONS.ENDPOINT_READ]}>
              <LazyRoute>
                <VelociraptorEndpointDetailPage />
              </LazyRoute>
            </RequirePermission>
          }
        />
        <Route
          path="/endpoints/:endpointId"
          element={
            <RequirePermission anyOf={[PERMISSIONS.ENDPOINT_READ]}>
              <LazyRoute>
                <EndpointDetailPage />
              </LazyRoute>
            </RequirePermission>
          }
        />
        <Route
          path="/endpoints"
          element={
            <RequirePermission anyOf={[PERMISSIONS.ENDPOINT_READ]}>
              <LazyRoute>
                <EndpointsPage />
              </LazyRoute>
            </RequirePermission>
          }
        />
        <Route path="/software-licences" element={<RequirePermission anyOf={[PERMISSIONS.SOFTWARE_LICENCE_READ]}><LazyRoute><SoftwareLicencesPage /></LazyRoute></RequirePermission>} />
        <Route path="/software-licences/:licenceId" element={<RequirePermission anyOf={[PERMISSIONS.SOFTWARE_LICENCE_READ]}><LazyRoute><SoftwareLicencesPage /></LazyRoute></RequirePermission>} />
        <Route
          path="/discovery"
          element={
            <RequirePermission anyOf={[PERMISSIONS.DISCOVERY_READ]}>
              <LazyRoute>
                <DiscoveryPage />
              </LazyRoute>
            </RequirePermission>
          }
        />
        <Route
          path="/service-desk"
          element={
            <RequirePermission anyOf={[PERMISSIONS.TICKET_READ, PERMISSIONS.TICKET_READ_OWN]}>
              <LazyRoute>
                <ServiceDeskPage />
              </LazyRoute>
            </RequirePermission>
          }
        />
        <Route
          path="/service-desk/:ticketId"
          element={
            <RequirePermission anyOf={[PERMISSIONS.TICKET_READ, PERMISSIONS.TICKET_READ_OWN]}>
              <LazyRoute>
                <TicketDetailPage />
              </LazyRoute>
            </RequirePermission>
          }
        />
        {MAINTENANCE_ROUTES.map(({ path, anyOf, Page }) => (
          <Route
            key={path}
            path={path}
            element={
              <RequirePermission anyOf={anyOf}>
                <LazyRoute>
                  <Page />
                </LazyRoute>
              </RequirePermission>
            }
          />
        ))}
        <Route
          path="/imports"
          element={
            <RequirePermission anyOf={[PERMISSIONS.IMPORT_READ, PERMISSIONS.IMPORT_VALIDATE]}>
              <LazyRoute>
                <ImportsPage />
              </LazyRoute>
            </RequirePermission>
          }
        />

        <Route
          path="/notifications"
          element={
            <LazyRoute>
              <NotificationsPage />
            </LazyRoute>
          }
        />

        <Route
          path="/platform"
          element={
            // A company's own address opens at /platform too; for anyone but the operator that is
            // simply their company's home, not a refusal.
            <PlatformOrHome>
              <LazyRoute>
                <PlatformPage />
              </LazyRoute>
            </PlatformOrHome>
          }
        />
        <Route
          path="/platform/tenants/:organizationId"
          element={
            <RequirePermission anyOf={[PERMISSIONS.PLATFORM_READ]}>
              <LazyRoute>
                <TenantDetailPage />
              </LazyRoute>
            </RequirePermission>
          }
        />
        <Route
          path="/platform/plans"
          element={
            <RequirePermission anyOf={[PERMISSIONS.PLATFORM_READ]}>
              <LazyRoute>
                <PlansPage />
              </LazyRoute>
            </RequirePermission>
          }
        />

        <Route
          path="/settings"
          element={
            <LazyRoute>
              <SettingsPage />
            </LazyRoute>
          }
        />
        <Route
          path="/user-manual/:articleId?"
          element={
            <LazyRoute>
              <UserManualPage />
            </LazyRoute>
          }
        />
        <Route
          path="/modules/:moduleId"
          element={
            <LazyRoute>
              <ModuleRedirectPage />
            </LazyRoute>
          }
        />
        <Route path="/403" element={<ForbiddenPage />} />
        <Route path="*" element={<NotFoundPage />} />
      </Route>
    </Routes>
  );
}

function LazyRoute({ children }: { children: React.ReactNode }) {
  return <Suspense fallback={<DetailSkeleton />}>{children}</Suspense>;
}
